import type { ChatModelAdapter, ThreadMessage } from '@assistant-ui/react'
import { isAxiosError } from 'axios'
import { useBudgetWarning } from '@/contexts/BudgetWarningContext'
import {
  getActiveRemoteThreadId,
  setActiveRemoteThreadId,
} from '@/runtime/activeThreadId'
import api from '@/services/api'
import type {
  ChatMessageResponse,
  InquisitorResponseData,
  ThreadResponse,
} from '@/types/chat'

function getLatestUserText(messages: readonly ThreadMessage[]): string {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message.role !== 'user') continue

    return message.content
      .filter((part) => part.type === 'text')
      .map((part) => part.text)
      .join('\n')
      .trim()
  }
  return ''
}

function errorResult(message: string): {
  content: Array<
    | { type: 'text'; text: string }
    | { type: 'data'; name: 'inquisitor-response'; data: InquisitorResponseData }
  >
  metadata: { custom: InquisitorResponseData }
} {
  const responseData: InquisitorResponseData = {
    log_id: '',
    response_type: 'error',
    message,
    soft_warning: false,
    spec: null,
  }
  return {
    content: [
      { type: 'text', text: message },
      {
        type: 'data',
        name: 'inquisitor-response',
        data: responseData,
      },
    ],
    metadata: {
      custom: { ...responseData },
    },
  }
}

async function resolveThreadId(
  unstableThreadId: string | undefined,
): Promise<string | undefined> {
  if (unstableThreadId) {
    setActiveRemoteThreadId(unstableThreadId)
    return unstableThreadId
  }

  const cached = getActiveRemoteThreadId()
  if (cached) return cached

  // Last resort: create a thread so the user message isn't dropped.
  try {
    const { data } = await api.post<ThreadResponse>('/threads', {})
    setActiveRemoteThreadId(data.id)
    return data.id
  } catch {
    return undefined
  }
}

export function createChatModelAdapter(
  onSoftWarning?: (active: boolean) => void,
): ChatModelAdapter {
  return {
    async run({ messages, abortSignal, unstable_threadId }) {
      const content = getLatestUserText(messages)
      if (!content) {
        return errorResult('Please enter a question.')
      }

      const threadId = await resolveThreadId(unstable_threadId)
      if (!threadId) {
        return errorResult(
          'Could not start this chat. Please click New chat and try again.',
        )
      }

      try {
        const { data } = await api.post<ChatMessageResponse>(
          `/threads/${threadId}/messages`,
          { content },
          { signal: abortSignal },
        )

        if (data.soft_warning) {
          onSoftWarning?.(true)
        }

        const responseData: InquisitorResponseData = {
          log_id: data.log_id,
          response_type: data.response_type,
          message: data.message,
          soft_warning: data.soft_warning,
          spec: data.spec ?? null,
          debug: data.debug ?? null,
        }

        return {
          content: [
            { type: 'text', text: data.message },
            {
              type: 'data',
              name: 'inquisitor-response',
              data: responseData,
            },
          ],
          metadata: {
            custom: { ...responseData },
          },
        }
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 429) {
          const detail = error.response.data?.detail
          const message =
            typeof detail === 'string'
              ? detail
              : 'Monthly usage budget reached. Check back next month or contact Taylor.'
          return errorResult(message)
        }

        const message =
          isAxiosError(error) && typeof error.response?.data?.detail === 'string'
            ? error.response.data.detail
            : 'Something went wrong while sending your question. Please try again.'

        return errorResult(message)
      }
    },
  }
}

export function useChatModelAdapter(): ChatModelAdapter {
  const { setSoftWarning } = useBudgetWarning()
  return createChatModelAdapter(setSoftWarning)
}
